/**
 * Bank Entry Store - Zustand state management for Bank Entry workflow
 * Manages file upload, parsing, classification, and voucher creation state
 */

import { create } from 'zustand';

// Types
export interface BankStatementRow {
  rowIndex: number;
  date: string;
  narration: string;
  debit: number | null;
  credit: number | null;
  ledger: string;
  vchType: 'Payment' | 'Receipt' | 'Contra';
  confidence: 'high' | 'medium' | 'low';
  isEdited: boolean;
  originalLedger?: string;
  originalVchType?: string;
  patternId?: string;  // Internal - for corrections (never shown to user)
}

export interface LedgerOption {
  name: string;
  group: string;
}

export interface PendingEdit {
  rowIndex: number;
  field: 'ledger' | 'vchType';
  oldValue: string;
  newValue: string;
}

export interface VoucherResult {
  rowIndex: number;
  status: string;
  voucherNumber?: string;
  error?: string;
}

export interface BankEntryState {
  // Step tracking
  currentStep: 'upload' | 'select' | 'parsing' | 'preview' | 'processing' | 'complete';

  // File state
  file: File | null;
  fileName: string;
  fileUrl: string;

  // Selection state
  selectedCompany: string;
  selectedBankAccount: string;
  companies: string[];
  bankAccounts: LedgerOption[];
  allLedgers: LedgerOption[];

  // Data state
  rows: BankStatementRow[];
  totalRows: number;
  matchedRows: number;      // Pattern matched (high confidence)
  llmClassifiedRows: number; // LLM classified (medium confidence)
  unmatchedRows: number;     // Still unclassified (low/none confidence)

  // Pagination
  pageSize: number;
  currentPage: number;

  // Processing state
  isLoading: boolean;
  isClassifying: boolean;
  classificationProgress: number;
  error: string | null;
  requiresPassword: boolean;
  filePassword: string;

  // Results
  createdVouchers: number;
  failedVouchers: number;
  paymentCount: number;
  receiptCount: number;
  contraCount: number;
  voucherResults: VoucherResult[];  // Per-row results for download

  // Background job tracking (for both parsing and voucher creation)
  jobId: string | null;
  jobProgress: number;
  jobProgressMessage: string;

  // Parse job tracking (separate from voucher job)
  parseJobId: string | null;
  parseProgress: number;
  parseProgressMessage: string;

  // Pattern learning dialog
  pendingEdit: PendingEdit | null;
  showPatternDialog: boolean;
}

export interface BankEntryActions {
  // File actions
  setFile: (file: File | null) => void;
  setFileUrl: (url: string) => void;

  // Selection actions
  setSelectedCompany: (company: string) => void;
  setSelectedBankAccount: (account: string) => void;
  setCompanies: (companies: string[]) => void;
  setBankAccounts: (accounts: LedgerOption[]) => void;
  setAllLedgers: (ledgers: LedgerOption[]) => void;

  // Data actions
  setRows: (rows: BankStatementRow[]) => void;
  updateRow: (rowIndex: number, updates: Partial<BankStatementRow>) => void;
  updateMultipleRows: (rowIndices: number[], updates: Partial<BankStatementRow>) => void;
  setStats: (matched: number, llmClassified: number, unmatched: number, total: number) => void;

  // Pagination actions
  setPageSize: (size: number) => void;
  setCurrentPage: (page: number) => void;

  // Step actions
  setCurrentStep: (step: BankEntryState['currentStep']) => void;

  // Processing actions
  setLoading: (loading: boolean) => void;
  setClassifying: (classifying: boolean) => void;
  setClassificationProgress: (progress: number) => void;
  setError: (error: string | null) => void;
  setRequiresPassword: (requires: boolean) => void;
  setFilePassword: (password: string) => void;

  // Results actions
  setResults: (created: number, failed: number, payment?: number, receipt?: number, contra?: number, results?: VoucherResult[]) => void;

  // Job tracking actions
  setJobId: (jobId: string) => void;
  setJobStatus: (progress: number, message: string) => void;

  // Parse job tracking actions
  setParseJobId: (jobId: string) => void;
  setParseJobStatus: (progress: number, message: string) => void;

  // Pattern learning
  setPendingEdit: (edit: PendingEdit | null) => void;
  setShowPatternDialog: (show: boolean) => void;
  triggerLedgerEdit: (rowIndex: number, newLedger: string) => void;

  // Reset
  reset: () => void;
}

const initialState: BankEntryState = {
  currentStep: 'upload',
  file: null,
  fileName: '',
  fileUrl: '',
  selectedCompany: '',
  selectedBankAccount: '',
  companies: [],
  bankAccounts: [],
  allLedgers: [],
  rows: [],
  totalRows: 0,
  matchedRows: 0,
  llmClassifiedRows: 0,
  unmatchedRows: 0,
  pageSize: 50,
  currentPage: 1,
  isLoading: false,
  isClassifying: false,
  classificationProgress: 0,
  error: null,
  requiresPassword: false,
  filePassword: '',
  createdVouchers: 0,
  failedVouchers: 0,
  paymentCount: 0,
  receiptCount: 0,
  contraCount: 0,
  voucherResults: [],
  jobId: null,
  jobProgress: 0,
  jobProgressMessage: '',
  parseJobId: null,
  parseProgress: 0,
  parseProgressMessage: '',
  pendingEdit: null,
  showPatternDialog: false,
};

export const useBankEntryStore = create<BankEntryState & BankEntryActions>((set, get) => ({
  ...initialState,

  // File actions
  setFile: (file) => set({
    file,
    fileName: file?.name || '',
    error: null,
    requiresPassword: false,
    filePassword: '',
  }),

  setFileUrl: (fileUrl) => set({ fileUrl }),

  // Selection actions
  setSelectedCompany: (selectedCompany) => set({
    selectedCompany,
    selectedBankAccount: '', // Reset bank account when company changes
    bankAccounts: [],
  }),

  setSelectedBankAccount: (selectedBankAccount) => set({ selectedBankAccount }),

  setCompanies: (companies) => set({ companies }),

  setBankAccounts: (bankAccounts) => set({ bankAccounts }),

  setAllLedgers: (allLedgers) => set({ allLedgers }),

  // Data actions
  setRows: (rows) => set({
    rows,
    totalRows: rows.length,
  }),

  updateRow: (rowIndex, updates) => set((state) => ({
    rows: state.rows.map((row) =>
      row.rowIndex === rowIndex
        ? { ...row, ...updates, isEdited: true }
        : row
    ),
  })),

  updateMultipleRows: (rowIndices, updates) => set((state) => ({
    rows: state.rows.map((row) =>
      rowIndices.includes(row.rowIndex)
        ? { ...row, ...updates, isEdited: true }
        : row
    ),
  })),

  setStats: (matchedRows, llmClassifiedRows, unmatchedRows, totalRows) => set({
    matchedRows,
    llmClassifiedRows,
    unmatchedRows,
    totalRows,
  }),

  // Pagination actions
  setPageSize: (pageSize) => set({ pageSize, currentPage: 1 }),
  setCurrentPage: (currentPage) => set({ currentPage }),

  // Step actions
  setCurrentStep: (currentStep) => set({ currentStep }),

  // Processing actions
  setLoading: (isLoading) => set({ isLoading }),

  setClassifying: (isClassifying) => set({ isClassifying }),

  setClassificationProgress: (classificationProgress) => set({ classificationProgress }),

  setError: (error) => set({ error }),

  setRequiresPassword: (requiresPassword) => set({ requiresPassword }),

  setFilePassword: (filePassword) => set({ filePassword }),

  // Results actions
  setResults: (createdVouchers, failedVouchers, paymentCount = 0, receiptCount = 0, contraCount = 0, voucherResults = []) => set({
    createdVouchers,
    failedVouchers,
    paymentCount,
    receiptCount,
    contraCount,
    voucherResults,
  }),

  // Job tracking actions
  setJobId: (jobId) => set({ jobId }),

  setJobStatus: (jobProgress, jobProgressMessage) => set({ jobProgress, jobProgressMessage }),

  // Parse job tracking actions
  setParseJobId: (parseJobId) => set({ parseJobId }),

  setParseJobStatus: (parseProgress, parseProgressMessage) => set({ parseProgress, parseProgressMessage }),

  // Pattern learning
  setPendingEdit: (pendingEdit) => set({ pendingEdit }),

  setShowPatternDialog: (showPatternDialog) => set({ showPatternDialog }),

  triggerLedgerEdit: (rowIndex, newLedger) => {
    const state = get();
    const row = state.rows.find(r => r.rowIndex === rowIndex);
    if (!row) return;

    const oldLedger = row.ledger;

    // If same ledger, no change needed
    if (oldLedger === newLedger) return;

    // Always show pattern learning dialog for any ledger change
    // Even Suspense mappings can have patterns (e.g., "BANK CHARGES" → Suspense)
    // This helps group similar transactions for future batch resolution
    set({
      pendingEdit: {
        rowIndex,
        field: 'ledger',
        oldValue: oldLedger,
        newValue: newLedger,
      },
      showPatternDialog: true,
    });
  },

  // Reset
  reset: () => set(initialState),
}));
